import React, { useState } from "react";

import { mdiBroom, mdiTrashCanOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { useTranslation } from "react-i18next";

import prettyDurations, { DURATION_UNITS } from "../../../utils/durations";
import Button, { BUTTON_VARIANTS } from "../../button/Button";
import ConfirmModal from "../../modal/ConfirmModal";
import Tooltip from "../../tooltip/Tooltip";
import AbilityContainer from "../../utils/abilities/AbilityContainer";

import type { JSX } from "react";

import type { ScheduleVariantDetails } from "../../../api/FastAPI.types";

interface Props {
  variantList: ScheduleVariantDetails[];
  deleteScheduleMutation: (scheduleId: { scheduleId: string }) => void;
  isDeleteDisabled: boolean;
}

export default function CalendarDaySidepanelVariantList({
  variantList,
  deleteScheduleMutation,
  isDeleteDisabled,
}: Props): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");
  const [showModal, setShowModal] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState<string>("");

  const rowClassNames = "flex items-center justify-between py-2";
  const firstColumnClassNames = "basis-4/12";
  const secondColumnClassNames = "basis-4/12 text-center";
  const thirdColumnClassNames = "basis-3/12 text-center";
  const fourthColumnClassNames = "basis-1/12 flex justify-end";

  const handleOpenModal = (sheduleId: string): void => {
    setSelectedVariantId(sheduleId);
    setShowModal(true);
  };

  const handleDeleteVariant = (): void => {
    setShowModal(false);
    deleteScheduleMutation({
      scheduleId: selectedVariantId,
    });
  };

  const confirmModalActions = [
    {
      label: t("cancel"),
      onClick: (): void => setShowModal(false),
      buttonVariant: BUTTON_VARIANTS.SECONDARY,
      isFullWidth: true,
    },
    {
      label: t("delete"),
      onClick: handleDeleteVariant,
      buttonVariant: BUTTON_VARIANTS.DELETE,
      isFullWidth: true,
    },
  ];

  return (
    <div className="divide-border border-border bg-muted divide-y rounded-[0.3125rem] border px-[0.625rem]">
      <div className={rowClassNames}>
        <div className={firstColumnClassNames}>{t("variants_name")}</div>
        <div className={secondColumnClassNames}>{t("media_count")}</div>
        <div className={thirdColumnClassNames}>{t("duration")}</div>
        <AbilityContainer abilityList={["fastChannelsWrite"]}>
          <div className={fourthColumnClassNames}>{t("actions")}</div>
        </AbilityContainer>
      </div>
      {variantList?.map((variant) => (
        <div key={variant.name} className={rowClassNames}>
          <div className={firstColumnClassNames}>{variant.name}</div>
          <div className={secondColumnClassNames}>{variant.items}</div>
          <div className={thirdColumnClassNames}>
            {
              prettyDurations(variant?.duration, {
                valueUnit: DURATION_UNITS.SECONDS,
              }).humanised
            }
          </div>
          <AbilityContainer abilityList={["fastChannelsWrite"]}>
            <div className={fourthColumnClassNames}>
              <Button
                data-tooltip-id="disabled-delete-variant"
                isDisabled={isDeleteDisabled}
                onClick={() => handleOpenModal(variant.uuid)}
                size="sm"
                type="button"
                variant={BUTTON_VARIANTS.DELETE}
              >
                <Icon path={mdiTrashCanOutline} size={0.78125} />
              </Button>
              {isDeleteDisabled ? (
                <Tooltip
                  content={t("calendarPlaylistTooltipDeletePastError")}
                  id="disabled-delete-variant"
                />
              ) : null}
            </div>
          </AbilityContainer>
        </div>
      ))}
      <ConfirmModal
        actions={confirmModalActions}
        iconPath={mdiBroom}
        isOpen={showModal}
        onClose={(): void => setShowModal(false)}
        size="sm"
        title={t("delete_variant_title")}
      >
        {t("delete_variant_message")}
      </ConfirmModal>
    </div>
  );
}
